// frontend/src/components/ProgressDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Footer } from './Footer';
import { apiUrl } from '../config/api';
import { CheckCircle, Clock, Target, BookOpen } from 'lucide-react';

interface User {
  id: string;
  email: string;
  fullName: string;
  company: string;
  role: string;
  status: string;
}

interface ProgressDashboardProps {
  user: User;
  onLogout: () => void;
}

interface ModuleProgress {
  moduleId: number;
  title: string;
  progress: {
    completedLessons: number;
    totalLessons: number;
    lastAccessedAt: number | null;
  };
}

interface OverallStats {
  totalModules: number;
  completedModules: number;
  totalLessons: number;
  completedLessons: number;
  totalTimeSpent: number;
  streakDays: number;
}

export function ProgressDashboard({ user, onLogout }: ProgressDashboardProps) {
  const navigate = useNavigate();
  const [modules, setModules] = useState<ModuleProgress[]>([]);
  const [stats, setStats] = useState<OverallStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(apiUrl('/api/modules'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('authToken');
            window.location.href = '/auth';
            return;
          }
          throw new Error('Failed to fetch progress');
        }

        const data = await response.json();
        setModules(data);

        // Calculate overall stats
        const totalLessons = data.reduce((acc: number, module: any) => acc + module.progress.totalLessons, 0);
        const completedLessons = data.reduce((acc: number, module: any) => acc + module.progress.completedLessons, 0);
        const completedModules = data.filter((module: any) => 
          module.progress.completedLessons === module.progress.totalLessons && module.progress.totalLessons > 0
        ).length;

        setStats({
          totalModules: data.length,
          completedModules,
          totalLessons,
          completedLessons,
          totalTimeSpent: 0, // Calculate from individual lesson progress
          streakDays: 0 // Calculate based on daily activity
        });
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, []);

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'var(--white)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <main className="dashboard-content" style={{ flex: 1 }}>
          <div className="container">
            <div className="loading-container">Loading progress...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'var(--white)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div className="container" style={{ flex: 1 }}>
          <section className="progress-section">
            <div className="progress-header">
              <h1>Your Learning Progress</h1>
              <p>Track your journey through the principal IT talent sourcing course</p>
            </div>

            {/* Overall Statistics */}
            {stats && (
              <div className="progress-overview">
                <div className="overview-card">
                  <div className="overview-icon">
                    <Target />
                  </div>
                  <div className="overview-content">
                    <h3>Overall Progress</h3>
                    <p className="overview-value">
                      {stats.totalLessons > 0 ? Math.round((stats.completedLessons / stats.totalLessons) * 100) : 0}%
                    </p>
                  </div>
                </div>

                <div className="overview-card">
                  <div className="overview-icon">
                    <CheckCircle />
                  </div>
                  <div className="overview-content">
                    <h3>Lessons Completed</h3>
                    <p className="overview-value">
                      {stats.completedLessons} / {stats.totalLessons}
                    </p>
                  </div>
                </div>

                <div className="overview-card">
                  <div className="overview-icon">
                    <Clock />
                  </div>
                  <div className="overview-content">
                    <h3>Modules Completed</h3>
                    <p className="overview-value">
                      {stats.completedModules} / {stats.totalModules}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Module Progress List */}
            <div className="module-progress-list">
              {modules.map((module) => {
                const progressPercentage = module.progress.totalLessons > 0 
                  ? (module.progress.completedLessons / module.progress.totalLessons) * 100 
                  : 0;

                return (
                  <div key={module.moduleId} className="module-progress-card">
                    <div className="module-progress-header">
                      <BookOpen className="module-icon" />
                      <h2>{module.title}</h2>
                    </div>

                    <div className="progress-stats">
                      <div className="progress-stat">
                        <span className="stat-label">Completion</span>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                        <span className="progress-percentage">
                          {Math.round(progressPercentage)}%
                        </span>
                      </div>

                      <div className="progress-details">
                        <div className="detail-item">
                          <CheckCircle className="detail-icon" />
                          <span>
                            {module.progress.completedLessons} of {module.progress.totalLessons} lessons completed
                          </span>
                        </div>
                        {module.progress.lastAccessedAt && (
                          <div className="detail-item">
                            <Clock className="detail-icon" />
                            <span>
                              Last activity: {new Date(module.progress.lastAccessedAt).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/module/${module.moduleId}`)}
                      className="continue-button"
                    >
                      {module.progress.completedLessons === 0 ? 'Start Module' : 'Continue Learning'}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      <Footer />
    </div>
  );
}